import { Injectable } from '@angular/core';
import {
    IChecklist2ReportAnswer,
    IChecklist2ReportChecklistUnits,
    IChecklist2ReportChecklistSummary,
    IBusinessUnitForReport,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
    IChecklist2ReportHistoryUnits,
    IChecklist2ReportHistoryComplianceData,
    IChecklist2ReportComplianceItem,
} from '../shared/fi-checklist2-report';
import { FiChecklist2ReportType } from '../enums/fi-checklist2-report-type';
import { AppTextService } from '../../../app-text';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { FiFilterType } from '../../../fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { ISelectedFilterItem } from '../../../fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { FiSnackBarService } from '../../../fi-snackbar/snack-bar.service';
import { Checkpoint2StatusType } from '../../checklist-enum';
import { CheckpointRangeStatusOptions } from '../../checklist-consts';
import { INameIdObj, INameIdObjPartial } from '../../../fi-shared-interfaces';
import { IMediaBankFileReference } from '../../../media-bank';
import { cloneDeep, isDefined } from '../../../utils';
import { IChecklistStatusProgressData } from '../../checklist-v2';
import { uniqueWith } from '../../../utils/fi-uniq-wtih';
import { ChecklistV2ReportPages } from '../enums/fi-checklist2-report-pages.enum';
import { FiChecklist2ReportTimePeriodType } from '../enums/fi-checklist2-report-time-period-type';
import { flattenSimple } from '../../../utils/flatten-simple';

import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    FiRecentActivityDialogComponent,
    IChecklist2ReportRecentActivityComponentData,
} from '@fi-shared/checklist/fi-checklist2-report/components/recent-activity-report/recent-activity-widget/recent-activity-dialog/fi-recent-activity-dialog.component';
import { IReportChartData } from '@fi-shared/fi-report-pie-chart/fi-report-pie-chart';

export interface ISaveChecklist2ReportFilterArgs {
    updateCurrentFilter: boolean;
}

@Injectable()
export class FiChecklist2ReportHelperService {
    constructor(
        private appTextService: AppTextService,
        private fiSnackbarService: FiSnackBarService,
        private fiDialogsService: FiDialogsService,
    ) {}

    widgetNameByReportTypeMap: Partial<Record<FiChecklist2ReportType, string>> = {
        [FiChecklist2ReportType.OpenChecklists]: 'checklist.report_open_checklists',
        [FiChecklist2ReportType.AttentionRequired]: 'checklist.report_attention_required',
        [FiChecklist2ReportType.PastChecklists]: 'checklist.checklist_history',
    };
    
    widgetIconClassByReportTypeMap: Partial<Record<FiChecklist2ReportType, string>> = {
        [FiChecklist2ReportType.OpenChecklists]: 'far fa-ballot-check',
        [FiChecklist2ReportType.AttentionRequired]: 'far fa-circle-exclamation',
        [FiChecklist2ReportType.PastChecklists]: 'far fa-clock-rotate-left',
    };

    getAvailableChecklistReportWidgetsData(isAuditMode: boolean): any[] {
        const availableChecklistReportWidgetsData = [];
        if (
            this.isReportTypeAvailable(
                FiChecklist2ReportType.OpenChecklists,
                isAuditMode,
            )
        ) {
            availableChecklistReportWidgetsData.push({
                Id: FiChecklist2ReportType.OpenChecklists,
                Name: this.widgetNameByReportTypeMap[FiChecklist2ReportType.OpenChecklists],
                IconClassName: this.widgetIconClassByReportTypeMap[FiChecklist2ReportType.OpenChecklists],
            });
        }
        if (
            this.isReportTypeAvailable(
                FiChecklist2ReportType.AttentionRequired,
                isAuditMode,
            )
        ) {
            availableChecklistReportWidgetsData.push({
                Id: FiChecklist2ReportType.AttentionRequired,
                Name: this.widgetNameByReportTypeMap[FiChecklist2ReportType.AttentionRequired],
                IconClassName: this.widgetIconClassByReportTypeMap[FiChecklist2ReportType.AttentionRequired],
            });
        }
        if (
            this.isReportTypeAvailable(
                FiChecklist2ReportType.PastChecklists,
                isAuditMode,
            )
        ) {
            availableChecklistReportWidgetsData.push({
                Id: FiChecklist2ReportType.PastChecklists,
                Name: this.widgetNameByReportTypeMap[FiChecklist2ReportType.PastChecklists],
                IconClassName: this.widgetIconClassByReportTypeMap[FiChecklist2ReportType.PastChecklists],
            });
        }

        return availableChecklistReportWidgetsData;
    }

    getChecklistReportSectionNameByType(reportType: FiChecklist2ReportType): string {
        switch (reportType) {
            case FiChecklist2ReportType.AttentionRequired:
                return this.appTextService.get('checklist.report_attention_required');
            case FiChecklist2ReportType.RecentActivity:
                return this.appTextService.get('common.recent_activity');
            case FiChecklist2ReportType.PastChecklists:
                return this.appTextService.get('checklist.checklist_history');
            case FiChecklist2ReportType.OpenChecklists:
                return this.appTextService.get('checklist.report_open_checklists');
        }
    }

    getWidgetDescription(reportParameter: IChecklistV2ReportParameter, skipCustomDescription: boolean = false): string {
        if (reportParameter.Description && !skipCustomDescription) return reportParameter.Description;

        if (reportParameter.ReportType === FiChecklist2ReportType.OpenChecklists)
            return this.appTextService.get('checklist.report_open_checklist_description');

        if (reportParameter.ReportType === FiChecklist2ReportType.AttentionRequired)
            return this.appTextService.get('checklist.report_deviations_description');

        if (reportParameter.ReportType === FiChecklist2ReportType.PastChecklists)
            return this.appTextService.get('checklist.report_checklist_history_description');

        if (reportParameter.ReportType === FiChecklist2ReportType.RecentActivity)
            return this.appTextService.get('checklist.report_recent_activity_description');
    }

    convertReportFilterSelectedIdToItem(
        selectedIds: number[] = [],
        availableItems: INameIdObjPartial[],
        filterType: FiFilterType,
        nameProp: string = 'Name',
    ): ISelectedFilterItem[] {
        return selectedIds.map((selectedId: number) => {
            const selectedItem: INameIdObjPartial = availableItems?.find((item: INameIdObjPartial) => {
                return item.Id === selectedId;
            });
            return {
                value: selectedId,
                name: selectedItem?.[nameProp],
                filterType,
            };
        });
    }

    getChecklistRemainingTime(endTime: string): string {
        const now = new Date();
        const diffMs = new Date(endTime).getTime() - now.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        if (diffMinutes < 1) {
            return `<span class="red-color">${this.appTextService.get('common.minutes_left', { '{0}': 0 })}</span>`;
        }
        if (diffMinutes < 60) {
            return `<span class="red-color">${this.getTimeString(diffMinutes, 'common.minute_left', 'common.minutes_left')}</span>`;
        }
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) {
            return `<span class="red-color">${this.appTextService.get('common.h_left', { '{0}': diffHours })}</span>`;
        }
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) {
            return `<span>${this.getTimeString(diffDays, 'common.day_left', 'common.days_left')}</span>`;
        }
        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks < 5) {
            return `<span>${this.getTimeString(diffWeeks, 'common.week_left', 'common.weeks_left')}</span>`;
        }
        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) {
            return `<span>${this.getTimeString(diffMonths, 'common.month_left', 'common.months_left')}</span>`;
        }
        const diffYears = Math.floor(diffMonths / 12);
        return `<span>${this.getTimeString(diffYears, 'common.year_left', 'common.years_left')}</span>`;
    }

    getTimeString(time: number, singleTextId: string, pluralTextId: string): string {
        return this.appTextService.get(time === 1 ? singleTextId : pluralTextId, { '{0}': time });
    }

    getDeviationsChartData(value: number): IReportChartData[] {
        return [
            {
                gradientColorLight: '#FF897D',
                gradientColorBright: '#DE3730',
                value,
            },
        ];
    }

    getNotOkCheckpoints(answerDetails: IChecklist2ReportAnswer[]): IChecklist2ReportAnswer[] {
        return answerDetails.filter((answerDetail: IChecklist2ReportAnswer) => {
            return answerDetail.AnswerStatus === Checkpoint2StatusType.NotOk;
        });
    }

    getAnswerStatusLabel(answerStatus: Checkpoint2StatusType): string {
        return this.appTextService.get(
            CheckpointRangeStatusOptions.find((statusOption: INameIdObj) => {
                return statusOption.Id === answerStatus;
            }).Name,
        );
    }

    getCreatorDataFromAnswerAttachment(answerWithAttachment: IChecklist2ReportAnswer): INameIdObj[] {
        return answerWithAttachment.MediaBankFileReferences.map((mediaBankFileRef: IMediaBankFileReference) => {
            return { Id: mediaBankFileRef.CreatedByUserId, Name: mediaBankFileRef.CreatedByUserName };
        });
    }

    // todo use FiChecklist2ReportFilterService
    getAvailableNotOkAnswerDetails(
        availableBusinessUnits: IBusinessUnitForReport[],
        notOkAnswerDetails: IChecklist2ReportAnswer[],
    ): IChecklist2ReportAnswer[] {
        const reportDataBusinessUnitIds: number[] = availableBusinessUnits.map(
            (unit: IBusinessUnitForReport) => unit.Id,
        );

        return notOkAnswerDetails.filter((answerDetails: IChecklist2ReportAnswer) => {
            return reportDataBusinessUnitIds.includes(answerDetails.BusinessUnitId);
        });
    }

    private getCurrentAnswerDetailsData(
        item: IChecklist2ReportAnswer,
        answerDetails: IChecklist2ReportAnswer[],
    ): IChecklist2ReportAnswer[] {
        return this.getUniqueAnswerDetailsByAnswerId(answerDetails).filter((answerDetail: IChecklist2ReportAnswer) => {
            return answerDetail.CheckpointId === item.CheckpointId && answerDetail.ScheduleId === item.ScheduleId;
        });
    }

    private getUniqueAnswerDetailsByAnswerId(answerDetails: IChecklist2ReportAnswer[]): IChecklist2ReportAnswer[] {
        return uniqueWith(answerDetails, (itemNew: IChecklist2ReportAnswer, itemOld: IChecklist2ReportAnswer) => {
            return (
                itemNew.ScheduleId === itemOld.ScheduleId &&
                itemNew.CheckpointId === itemOld.CheckpointId &&
                itemNew.AnswerId === itemOld.AnswerId
            );
        });
    }

    getDeviationsPercentage(checklistUnits: IChecklist2ReportHistoryUnits): number {
        const completedCheckpointsCount: number =
            checklistUnits.OkCheckpointsCount + checklistUnits.NotOkCheckpointsCount;
        if (!completedCheckpointsCount) return 0;

        return Math.round((checklistUnits.NotOkCheckpointsCount * 100) / completedCheckpointsCount);
    }

    getOkAnswersPercentage(checklistUnits: IChecklist2ReportHistoryUnits): number {
        const completedCheckpointsCount: number =
            checklistUnits.OkCheckpointsCount + checklistUnits.NotOkCheckpointsCount;
        if (!completedCheckpointsCount) return 0;

        return Math.round((checklistUnits.OkCheckpointsCount * 100) / completedCheckpointsCount);
    }

    getChecklistUnitsFilledCheckpoints(checklistUnit: IChecklist2ReportChecklistUnits): number {
        return checklistUnit.NotOkCheckpointsCount + checklistUnit.OkCheckpointsCount;
    }

    getChecklistUnitsTotalCheckpoints(checklistUnit: IChecklist2ReportChecklistUnits): number {
        return this.getChecklistUnitsFilledCheckpoints(checklistUnit) + checklistUnit.NotFilledCheckpointsCount;
    }

    getHistoryUnitsFilledCheckpoints(checklistUnit: IChecklist2ReportHistoryUnits): number {
        return checklistUnit.NotOkCheckpointsCount + checklistUnit.OkCheckpointsCount;
    }

    getHistoryUnitsTotalCheckpoints(checklistUnit: IChecklist2ReportHistoryUnits): number {
        return this.getHistoryUnitsFilledCheckpoints(checklistUnit) + checklistUnit.NotFilledCheckpointsCount;
    }

    isAutomatedActionAdded(item: IChecklist2ReportAnswer): boolean {
        return !!item.CheckpointActions.length;
    }

    isOneTimeTaskAdded(item: IChecklist2ReportAnswer): boolean {
        return !!item.OneTimeTaskIds?.length;
    }

    private _isPageNavBarHidden$ = new ReplaySubject<boolean>(1);

    setPageNavBarVisibility(isHidden: boolean) {
        this._isPageNavBarHidden$.next(isHidden);
    }

    getPageNavBarVisibility$(): Observable<boolean> {
        return this._isPageNavBarHidden$.asObservable();
    }

    private _newParameterCreated$ = new Subject<IChecklistV2ReportParameter>();

    onNewParameterAdded$(): Observable<IChecklistV2ReportParameter> {
        return this._newParameterCreated$.asObservable();
    }

    addNewParameter(widget: IChecklistV2ReportParameter) {
        this._newParameterCreated$.next(widget);
    }

    private _triggerAddNewWidgetOpenDialog = new Subject<void>();
    triggerAddNewWidgetOpenDialog() {
        this._triggerAddNewWidgetOpenDialog.next();
    }
    onTriggerAddNewWidgetOpenDialog$() {
        return this._triggerAddNewWidgetOpenDialog.asObservable();
    }

    private _isAddNewWidgetButtonShown = new BehaviorSubject<boolean>(true);
    get isAddNewWidgetButtonShown$(): Observable<boolean> {
        return this._isAddNewWidgetButtonShown.asObservable();
    }
    setIsAddNewWidgetButtonShown(isAddNewWidgetButtonShown: boolean) {
        this._isAddNewWidgetButtonShown.next(isAddNewWidgetButtonShown);
    }

    private _oneTimeTasksChanged$ = new ReplaySubject<IChecklist2ReportAnswer>(1);
    get oneTimeTasksChanged$(): Observable<IChecklist2ReportAnswer> {
        return this._oneTimeTasksChanged$.asObservable();
    }

    triggerOneTimeTasksChanged(answerDetail: IChecklist2ReportAnswer) {
        this._oneTimeTasksChanged$.next(answerDetail);
    }

    private _checkpointActionsChanged$ = new ReplaySubject<IChecklist2ReportAnswer>(1);
    get checkpointActionsChanged$(): Observable<IChecklist2ReportAnswer> {
        return this._checkpointActionsChanged$.asObservable();
    }

    triggerCheckpointActionsChanged(answerDetail: IChecklist2ReportAnswer) {
        this._checkpointActionsChanged$.next(answerDetail);
    }

    getCurrentReportPageByReportType(reportType: FiChecklist2ReportType): ChecklistV2ReportPages {
        if (reportType === FiChecklist2ReportType.OpenChecklists)
            return ChecklistV2ReportPages.WidgetDetailsOpenChecklists;
        if (reportType === FiChecklist2ReportType.AttentionRequired)
            return ChecklistV2ReportPages.WidgetDetailsDeviations;
        if (reportType === FiChecklist2ReportType.PastChecklists)
            return ChecklistV2ReportPages.WidgetDetailsPastChecklists;
    }

    getChecklistReportHistoryOptions(): INameIdObj[] {
        return [
            { Id: FiChecklist2ReportTimePeriodType.Today, Name: this.appTextService.get('today') },
            { Id: FiChecklist2ReportTimePeriodType.LastWeek, Name: this.appTextService.get('common.last_week') },
            { Id: FiChecklist2ReportTimePeriodType.LastMonth, Name: this.appTextService.get('common.last_month') },
            { Id: FiChecklist2ReportTimePeriodType.LastQuarter, Name: this.appTextService.get('common.last_quarter') },
            { Id: FiChecklist2ReportTimePeriodType.LastYear, Name: this.appTextService.get('common.last_year') },
        ];
    }

    private _navigateToDashboardTrigger$ = new Subject<void>();
    triggerNavigateToDashboard() {
        this._navigateToDashboardTrigger$.next();
    }
    onNavigateToDashboard$(): Observable<void> {
        return this._navigateToDashboardTrigger$.asObservable();
    }

    private _saveFilterAsNewWidget$ = new Subject<void>();
    saveFilterAsNewWidget() {
        this._saveFilterAsNewWidget$.next();
    }
    onSaveFilterAsNewWidget$(): Observable<void> {
        return this._saveFilterAsNewWidget$.asObservable();
    }

    private _saveFilter$ = new Subject<ISaveChecklist2ReportFilterArgs>();
    saveFilter(args: ISaveChecklist2ReportFilterArgs) {
        this._saveFilter$.next(args);
    }
    onSaveFilter$(): Observable<ISaveChecklist2ReportFilterArgs> {
        return this._saveFilter$.asObservable();
    }

    private _saveFilterFinished$ = new Subject<void>();
    triggerSaveFilterFinished() {
        this._saveFilterFinished$.next();
    }
    onSaveFilterFilterFinished$(): Observable<void> {
        return this._saveFilterFinished$.asObservable();
    }

    private _toggleWidgetDetailsFilterContainerVisibility$ = new Subject<void>();
    toggleWidgetDetailsFilterContainerVisibility() {
        this._toggleWidgetDetailsFilterContainerVisibility$.next();
    }
    onToggleWidgetDetailsFilterContainerVisibility$(): Observable<void> {
        return this._toggleWidgetDetailsFilterContainerVisibility$.asObservable();
    }

    private _saveFilterButtonVisibilityChange = new Subject<boolean>();
    changeSaveFilterButtonVisibilityChange(value: boolean) {
        this._saveFilterButtonVisibilityChange.next(value);
    }
    onSaveFilterButtonVisibilityChange$(): Observable<boolean> {
        return this._saveFilterButtonVisibilityChange.asObservable();
    }

    /*getCombinedBusinessUnitCheckpoints(checklistUnits: IChecklist2ReportChecklistUnits,
                                       checklistUnitIterationsData: IChecklist2ReportChecklistUnits[]): IChecklist2ReportChecklistUnits {
        checklistUnits = cloneDeep(checklistUnits);

        const mergeUnitsAnswersData = (key: string) =>
            checklistUnitIterationsData.flatMap((data: IChecklist2ReportChecklistUnits) => data[key] || []);

        checklistUnits.NotFilledCheckpointIds = mergeUnitsAnswersData('NotFilledCheckpointIds');
        checklistUnits.NotOkCheckpointIds = mergeUnitsAnswersData('NotOkCheckpointIds');
        checklistUnits.OkCheckpointIds = mergeUnitsAnswersData('OkCheckpointIds');
        checklistUnits.AnswerDetails = mergeUnitsAnswersData('AnswerDetails');

        return checklistUnits;
    }*/

    /*getPastChecklistUnitsCheckpointsCounter(checklistUnits: IChecklist2ReportChecklistUnits[]): IChecklist2ReportHistoryCheckpointsCount {
        const mergeCheckpointIds = (key: string) =>
            checklistUnits.flatMap((data: IChecklist2ReportChecklistUnits) => data[key] || []);

        return {
            notFilledCheckpointCount: mergeCheckpointIds('NotFilledCheckpointIds').length,
            notOkCheckpointCount: mergeCheckpointIds('NotOkCheckpointIds').length,
            okCheckpointCount: mergeCheckpointIds('OkCheckpointIds').length,
        }
    }*/

    getAnswersWithAttachments<T extends { AnswerDetails: IChecklist2ReportAnswer[] }>(
        checklistUnits: T[],
    ): IChecklist2ReportAnswer[] {
        const allAnswerDetails: IChecklist2ReportAnswer[] = flattenSimple(
            checklistUnits.map((checklistUnit) => checklistUnit.AnswerDetails),
        );

        return allAnswerDetails.filter(
            (answerDetail: IChecklist2ReportAnswer) => !!answerDetail.MediaBankFileReferences?.length,
        );
    }

    getChecklistSummaryCompletedIterationsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return checklistSummary.reduce(
            (partialSum: number, item: IChecklist2ReportChecklistSummary) => partialSum + item.CompletedIterationsCount,
            0,
        );
    }
    getChecklistSummaryInProgressIterattionsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return checklistSummary.reduce(
            (partialSum: number, item: IChecklist2ReportChecklistSummary) =>
                partialSum + item.InProgressIterationsCount,
            0,
        );
    }
    getChecklistSummaryNotStartedIterationsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return checklistSummary.reduce(
            (partialSum: number, item: IChecklist2ReportChecklistSummary) =>
                partialSum + item.NotStartedIterationsCount,
            0,
        );
    }
    getChecklistSummaryTotalUnitsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return (
            this.getChecklistSummaryCompletedIterationsCount(checklistSummary) +
            this.getChecklistSummaryInProgressIterattionsCount(checklistSummary) +
            this.getChecklistSummaryNotStartedIterationsCount(checklistSummary)
        );
    }
    getStatusProgressDataFromSummary(
        checklistSummary: IChecklist2ReportChecklistSummary[],
    ): IChecklistStatusProgressData {
        return {
            completedCount: this.getChecklistSummaryCompletedIterationsCount(checklistSummary),
            inProgressCount: this.getChecklistSummaryInProgressIterattionsCount(checklistSummary),
            notStartedCount: this.getChecklistSummaryNotStartedIterationsCount(checklistSummary),
        };
    }
    getNonComplianceDataFromSummary(
        checklistSummary: IChecklist2ReportChecklistSummary[],
    ): IChecklist2ReportHistoryComplianceData {
        const complianceThresholdEnabledChecklists: IChecklist2ReportChecklistSummary[] =
            this.getComplianceThresholdEnabledChecklists(checklistSummary);

        const allNonComplianceIterationsCount: number = this.getAllNonComplianceIterationsCount(
            complianceThresholdEnabledChecklists,
        );
        const allComplianceIterationsCount: number = this.getAllComplianceIterationsCount(
            complianceThresholdEnabledChecklists,
        );

        return {
            nonComplianceUnitsCount: allNonComplianceIterationsCount,
            complianceUnitsCount: allComplianceIterationsCount,
            iterationsCount: allNonComplianceIterationsCount + allComplianceIterationsCount,
        };
    }

    private getComplianceThresholdEnabledChecklists(
        checklistSummary: IChecklist2ReportChecklistSummary[],
    ): IChecklist2ReportChecklistSummary[] {
        return checklistSummary.filter((item: IChecklist2ReportChecklistSummary) => item.IsComplianceThresholdEnabled);
    }
    private getAllNonComplianceIterationsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return checklistSummary.reduce((partialSum: number, checklist: IChecklist2ReportChecklistSummary) => {
            return partialSum + checklist.NonComplianceIterationsCount;
        }, 0);
    }
    private getAllComplianceIterationsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return checklistSummary.reduce((partialSum: number, checklist: IChecklist2ReportChecklistSummary) => {
            return partialSum + checklist.ComplianceIterationsCount;
        }, 0);
    }

    /*private getComplianceThresholdEnabledUnitsCount(checklistSummary: IChecklist2ReportChecklistSummary[]): number {
        return checklistSummary.reduce((partialSum: number, checklist: IChecklist2ReportChecklistSummary) => {
            return partialSum + checklist.IterationsCount
        }, 0);
    }*/

    getNonCompliancePercentage(item: IChecklist2ReportComplianceItem): number {
        return Math.round((100 * item.NonComplianceIterationsCount) / this.getTotalComplianceEnabledIterations(item));
    }

    getNonCompliancePercentageText(item: IChecklist2ReportComplianceItem): string {
        return `${item.NonComplianceIterationsCount}/${this.getTotalComplianceEnabledIterations(item)}`;
    }

    getCompliancePercentage(item: IChecklist2ReportComplianceItem): number {
        return Math.round((100 * item.ComplianceIterationsCount) / this.getTotalComplianceEnabledIterations(item));
    }

    getCompliancePercentageText(item: IChecklist2ReportComplianceItem): string {
        return `${item.ComplianceIterationsCount}/${this.getTotalComplianceEnabledIterations(item)}`;
    }

    private getTotalComplianceEnabledIterations(item: IChecklist2ReportComplianceItem): number {
        const total = item.NonComplianceIterationsCount + item.ComplianceIterationsCount;
        return total !== 0 ? total : 1;
    }

    getStatusProgressDataFromUnits(checklistUnits: IChecklist2ReportHistoryUnits[]): IChecklistStatusProgressData {
        return {
            completedCount: checklistUnits.reduce(
                (partialSum: number, unit) => partialSum + unit.CompletedIterationsCount,
                0,
            ),
            inProgressCount: checklistUnits.reduce(
                (partialSum: number, unit) => partialSum + unit.InProgressIterationsCount,
                0,
            ),
            notStartedCount: checklistUnits.reduce(
                (partialSum: number, unit) => partialSum + unit.NotStartedIterationsCount,
                0,
            ),
        };
    }

    isCustomReportWidget(reportParameter: IChecklistV2ReportParameter) {
        return !!reportParameter.Name;
    }
    isFilterEmpty(filter: IChecklistV2ReportParameterFilter) {
        return (
            !filter.ChecklistIds?.length &&
            !filter.ScheduleIds?.length &&
            !filter.CheckpointGroupIds?.length &&
            !filter.CheckpointIds?.length &&
            !filter.BusinessAreaIds?.length &&
            !filter.AreaIds?.length &&
            !filter.BusinessUnitIds?.length &&
            !filter.UserIds?.length &&
            !filter.CheckpointStatusTypes?.length
        );
    }

    isFilterOnlyWithChecklistsSchedules(filter: IChecklistV2ReportParameterFilter) {
        return (
            !filter.CheckpointGroupIds?.length &&
            !filter.CheckpointIds?.length &&
            !filter.BusinessAreaIds?.length &&
            !filter.AreaIds?.length &&
            !filter.BusinessUnitIds?.length &&
            !filter.UserIds?.length &&
            !filter.CheckpointStatusTypes?.length
        );
    }

    isReportTypeAvailable(reportType: FiChecklist2ReportType, auditMode: boolean) {
        if (
            reportType === FiChecklist2ReportType.AttentionRequired ||
            reportType === FiChecklist2ReportType.PastChecklists ||
            reportType === FiChecklist2ReportType.OpenChecklists
        )
            return true;
        if (reportType === FiChecklist2ReportType.RecentActivity) return !auditMode;
    }

    // note: if you decide to change class names, you need to change them in fi-checklist-unit-benchmark-chart-row-container as well.
    private readonly benchmarkSmallContainerClassName: string = 'quarter';
    private readonly benchmarkMidContainerClassName: string = 'half';
    private readonly benchmarkBigContainerClassName: string = 'full';
    getChecklistBenchmarkCardSizeClassName(businessUnitIdsLength: number): string {
        if (businessUnitIdsLength <= 3) return this.benchmarkSmallContainerClassName;

        if (businessUnitIdsLength <= 10) return this.benchmarkMidContainerClassName;

        return this.benchmarkBigContainerClassName;
    }

    showRecentActivityDialog(recentActivityDialogData: IChecklist2ReportRecentActivityComponentData) {
        return this.fiDialogsService.custom({
            contentComponentRef: FiRecentActivityDialogComponent,
            contentComponentData: recentActivityDialogData,
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-content-no-bottom-padding'],
                autoFocus: false,
                restoreFocus: false,
                maxWidth: '910px',
            },
        });
    }

    private reportParamUpdate$ = new Subject<IChecklistV2ReportParameter>();
    onReportParamUpdate$(): Observable<IChecklistV2ReportParameter> {
        return this.reportParamUpdate$.asObservable();
    }

    triggerReportParamUpdated(reportParam: IChecklistV2ReportParameter) {
        this.reportParamUpdate$.next(reportParam);
    }
}
